/**
 * In-app Help & Demo Guide content (sales-enablement).
 *
 * Kept as plain data (not the central i18n bundle) because it's long-form prose
 * that only this page renders. Selected by `$i18n.locale` in Help.vue. Use the
 * `{brand}` token anywhere the client's brand name should appear — Help.vue
 * substitutes `this.$brand.name` at render time, so this stays white-label safe.
 *
 * Per-section presentation fields:
 *   icon   — mdi icon for the section's chip
 *   tone   — colour key: brand | green | warm | purple
 *   layout — 'list' (default) | 'steps' (numbered) | 'checks' (checklist)
 */
const help = {
  en: {
    title: "Help & Demo Guide",
    subtitle: "What this platform is, the problems it solves, and how to demo it to leads.",
    sections: [
      {
        icon: "information-outline", tone: "brand",
        title: "What is {brand}?",
        body: "{brand} is a field & workforce operations platform. It gives construction and field-service teams one place to plan projects, break them into tasks and resources, log the work that happens on site every day, and watch cost, hours and progress update in real time.",
        points: [
          "Plan projects and reuse them as templates",
          "Track tasks, personnel (internal & external), equipment and consumables",
          "Capture daily work logs from the field",
          "See live cost, profit, hours and progress in the Project Summary",
          "Works in English and French, and is fully white-label (brand, colour, logo)"
        ]
      },
      {
        icon: "lightbulb-outline", tone: "warm",
        title: "The problem it solves",
        body: "On most sites, hours, materials and progress are tracked on paper or scattered spreadsheets, so managers only learn that a project is over budget or behind schedule weeks later. {brand} centralises the data so the numbers are accurate and immediate.",
        points: [
          "No more guessing labour cost — internal vs external hours are split and priced",
          "Logged hours reconcile against the plan, and mismatches are flagged automatically",
          "Progress per task and per day is visible at a glance",
          "One source of truth instead of email + spreadsheets + chat threads"
        ]
      },
      {
        icon: "account-group", tone: "purple",
        title: "Who uses it",
        body: "Access is role-based, so each person sees exactly what they need.",
        points: [
          "System / Admin — set up the workspace, manage users, categories and every project",
          "Project Manager — create and plan projects, see all projects and their summaries",
          "Site Manager — log daily work and track progress for their assigned sites"
        ]
      },
      {
        icon: "sitemap", tone: "brand",
        title: "Core concepts",
        body: "The platform is organised around a simple hierarchy.",
        points: [
          "Project — a job/site, with budget, phase and managers",
          "Tasks, Personnel, Equipment, Consumables — what the project needs, each with an estimation (planned quantity, hours, cost)",
          "Work logs — daily entries recording quantity done and hours spent",
          "Project Summary — Overview (KPIs, daily profit, hours health), Per Day and Per Task views",
          "Templates & Categories — reusable project structures and shared vocabulary"
        ]
      },
      {
        icon: "play-circle-outline", tone: "green", layout: "steps",
        title: "The typical workflow",
        body: "A project moves through these steps:",
        points: [
          "Create a project — from scratch or from a template",
          "Add tasks and resources with their estimations (quantity, performance, crew, cost)",
          "Assign the project and site managers",
          "The field team logs work each day (quantities and hours)",
          "Track results in the Project Summary — profit, hours health, progress per task and day",
          "Export the data when you need to report or invoice"
        ]
      },
      {
        icon: "monitor", tone: "brand", layout: "checks",
        title: "How to run a demo",
        body: "A populated demo dataset is preloaded, so you can present immediately.",
        points: [
          "Sign in and open the Projects list — pick a populated project",
          "Open its Summary → Overview: walk the KPI tiles, the daily-profit chart and the hours-health ring",
          "Show Per Day and Per Task to demonstrate the level of detail",
          "Open a task or personnel record to show the estimation vs the logged work",
          "Use the header search (or ⌘K) to jump to any project or person",
          "Switch Language (EN/FR) and Database (English/French dataset) from the sidebar to show the bilingual, multi-tenant capability",
          "Mention white-labeling: the same product ships under any brand, colour and logo"
        ]
      },
      {
        icon: "star-outline", tone: "green",
        title: "Talking points for leads",
        body: "Lead with the outcomes, not the screens.",
        points: [
          "Real-time cost and profit visibility — know today, not at month-end",
          "Internal vs external labour, split and reconciled automatically",
          "Built for the field — fast daily logging, usable by non-technical crews",
          "Bilingual and white-label — deploy per client, in their language and brand",
          "One platform replacing paper, spreadsheets and chat threads"
        ]
      }
    ]
  },
  fr: {
    title: "Aide & Guide de démonstration",
    subtitle: "Ce qu'est la plateforme, les problèmes qu'elle résout et comment la présenter aux prospects.",
    sections: [
      {
        icon: "information-outline", tone: "brand",
        title: "Qu'est-ce que {brand} ?",
        body: "{brand} est une plateforme de gestion des opérations de chantier et de main-d'œuvre. Elle offre aux équipes de terrain un seul endroit pour planifier les projets, les décomposer en tâches et ressources, saisir le travail réalisé chaque jour, et suivre en temps réel les coûts, les heures et l'avancement.",
        points: [
          "Planifier des projets et les réutiliser comme modèles",
          "Suivre les tâches, le personnel (interne & externe), les équipements et les consommables",
          "Saisir les journaux de travail quotidiens depuis le terrain",
          "Visualiser en direct le coût, la marge, les heures et l'avancement dans le Résumé du projet",
          "Disponible en français et en anglais, et entièrement personnalisable (marque, couleur, logo)"
        ]
      },
      {
        icon: "lightbulb-outline", tone: "warm",
        title: "Le problème qu'elle résout",
        body: "Sur la plupart des chantiers, les heures, les matériaux et l'avancement sont suivis sur papier ou dans des tableurs dispersés ; les responsables découvrent trop tard qu'un projet dépasse le budget ou prend du retard. {brand} centralise les données pour qu'elles soient justes et immédiates.",
        points: [
          "Fini les estimations approximatives — les heures internes et externes sont distinguées et chiffrées",
          "Les heures saisies sont rapprochées du plan, et les écarts sont signalés automatiquement",
          "L'avancement par tâche et par jour est visible en un coup d'œil",
          "Une seule source de vérité au lieu d'e-mails, de tableurs et de messageries"
        ]
      },
      {
        icon: "account-group", tone: "purple",
        title: "Qui l'utilise",
        body: "Les accès sont basés sur les rôles : chacun voit exactement ce dont il a besoin.",
        points: [
          "Système / Admin — configure l'espace, gère les utilisateurs, les catégories et tous les projets",
          "Chef de projet — crée et planifie les projets, voit tous les projets et leurs résumés",
          "Chef de chantier — saisit le travail quotidien et suit l'avancement de ses chantiers"
        ]
      },
      {
        icon: "sitemap", tone: "brand",
        title: "Concepts clés",
        body: "La plateforme s'organise autour d'une hiérarchie simple.",
        points: [
          "Projet — un chantier, avec budget, phase et responsables",
          "Tâches, Personnel, Équipements, Consommables — les besoins du projet, chacun avec une estimation (quantité, heures, coût prévus)",
          "Journaux de travail — saisies quotidiennes de la quantité réalisée et des heures passées",
          "Résumé du projet — vues Aperçu (indicateurs, marge journalière, équilibre des heures), Par jour et Par tâche",
          "Modèles & Catégories — structures de projet réutilisables et vocabulaire partagé"
        ]
      },
      {
        icon: "play-circle-outline", tone: "green", layout: "steps",
        title: "Le déroulé type",
        body: "Un projet suit ces étapes :",
        points: [
          "Créer un projet — de zéro ou à partir d'un modèle",
          "Ajouter les tâches et ressources avec leurs estimations (quantité, rendement, effectif, coût)",
          "Affecter les chefs de projet et de chantier",
          "L'équipe de terrain saisit le travail chaque jour (quantités et heures)",
          "Suivre les résultats dans le Résumé du projet — marge, équilibre des heures, avancement par tâche et par jour",
          "Exporter les données pour le reporting ou la facturation"
        ]
      },
      {
        icon: "monitor", tone: "brand", layout: "checks",
        title: "Comment faire une démonstration",
        body: "Un jeu de données de démonstration est préchargé : vous pouvez présenter immédiatement.",
        points: [
          "Connectez-vous et ouvrez la liste des projets — choisissez un projet rempli",
          "Ouvrez son Résumé → Aperçu : présentez les indicateurs, le graphique de marge journalière et l'anneau d'équilibre des heures",
          "Montrez les vues Par jour et Par tâche pour illustrer le niveau de détail",
          "Ouvrez une tâche ou une fiche personnel pour montrer l'estimation face au travail saisi",
          "Utilisez la recherche de l'en-tête (ou ⌘K) pour atteindre n'importe quel projet ou personne",
          "Changez la Langue (FR/EN) et la Base de données (jeu français/anglais) depuis la barre latérale pour montrer le bilingue et le multi-tenant",
          "Mentionnez la personnalisation : le même produit se décline sous n'importe quelle marque, couleur et logo"
        ]
      },
      {
        icon: "star-outline", tone: "green",
        title: "Arguments pour les prospects",
        body: "Mettez en avant les bénéfices, pas les écrans.",
        points: [
          "Visibilité en temps réel du coût et de la marge — savoir aujourd'hui, pas en fin de mois",
          "Main-d'œuvre interne et externe, distinguée et rapprochée automatiquement",
          "Conçu pour le terrain — saisie quotidienne rapide, adaptée aux équipes non techniques",
          "Bilingue et personnalisable — déployé par client, dans sa langue et sa marque",
          "Une seule plateforme qui remplace papier, tableurs et fils de discussion"
        ]
      }
    ]
  }
};

export default help;
